import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

function Home() {
  const [lookupCount, setLookupCount] = useState(null);

  useEffect(() => {
    // Fetch the all-time lookup count
    fetch('/api/auth/lookup-count')
      .then(response => response.json())
      .then(data => {
        setLookupCount(data.allTimeSuccessfulLookups);
      })
      .catch(error => {
        console.error('Error fetching lookup count:', error);
        // Fallback to a static number if we can't get the real data
        setLookupCount(66357);
      });
  }, []);

  // Format number with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  return (
    <div className="home-container">
      <header className="hero">
        <div className="hero-content">
          {lookupCount && (
            <div className="lookup-counter">
              <span className="lookup-count">{formatNumber(lookupCount)}</span>
              <span className="lookup-label">ZIP Code Lookups</span>
              <span className="lookup-tag">and counting!</span>
            </div>
          )}
          <h1>Welcome to ZipCode AutoFill</h1>
          <p className="tagline">Streamline Your Address Management with One Simple ZIP Code</p>
          <div className="hero-buttons">
            <Link to="/register" className="btn btn-primary">Get Started</Link>
            <Link to="/setup-guide" className="btn btn-secondary">How It Works</Link>
          </div>
          <div className="hero-shapes">
            <div className="hero-shape hero-shape-1"></div>
            <div className="hero-shape hero-shape-2"></div>
            <div className="hero-shape hero-shape-3"></div>
          </div>
        </div>
      </header>

      <section className="section-benefits">
        <div className="section-header">
          <h2>Why Choose ZipCode AutoFill?</h2>
          <p className="section-subheading">Our solution helps businesses save time and improve data accuracy</p>
        </div>
        <div className="feature-grid">
          <div className="feature">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                <path d="M2 2l7.586 7.586"></path>
                <circle cx="11" cy="11" r="2"></circle>
              </svg>
            </div>
            <h3>Automated Address Completion</h3>
            <p>Instantly populate city, state, county, country, and timezone fields from just a ZIP code.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                <path d="M9 14h6"></path>
                <path d="M9 18h6"></path>
                <path d="M9 10h6"></path>
              </svg>
            </div>
            <h3>Seamless HubSpot Integration</h3>
            <p>Works flawlessly with your HubSpot CRM for both contacts and companies.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </div>
            <h3>Customizable Fields</h3>
            <p>Choose which fields to update based on your specific HubSpot setup.</p>
          </div>
          <div className="feature">
            <div className="feature-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </div>
            <h3>US ZIP Codes Only</h3>
            <p>Specialized for 5-digit US ZIP codes, ensuring accurate results for United States addresses.</p>
          </div>
        </div>
      </section>

      <section className="section-process">
        <div className="section-header">
          <h2>How It Works</h2>
          <p className="section-subheading">Get started in just a few simple steps</p>
        </div>
        <div className="process-steps">
          <div className="process-step">
            <div className="process-step-number">1</div>
            <div className="process-step-content">
              <h3>Connect Your HubSpot Account</h3>
              <p>Seamlessly link your HubSpot account to enable automatic address updates.</p>
            </div>
          </div>
          <div className="process-step">
            <div className="process-step-number">2</div>
            <div className="process-step-content">
              <h3>Configure Fields to Update</h3>
              <p>Choose which fields in HubSpot should be auto-filled based on your preferences.</p>
            </div>
          </div>
          <div className="process-step">
            <div className="process-step-number">3</div>
            <div className="process-step-content">
              <h3>Enter a ZIP Code</h3>
              <p>Simply input a ZIP code within your HubSpot account to trigger auto-fill functionality.</p>
            </div>
          </div>
          <div className="process-step">
            <div className="process-step-number">4</div>
            <div className="process-step-content">
              <h3>Auto-Populate Address Fields</h3>
              <p>Watch as city, state, and other fields are filled in automatically with precision!</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pricing">
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p className="section-subheading">Pay only for what you use. Purchase credits and use them as needed.</p>
        </div>
        <div className="pricing-cta">
          <Link to="/pricing" className="btn btn-secondary">View Pricing Plans</Link>
        </div>
      </section>

      <section className="section-cta">
        <div className="cta-container">
          <h2>Ready to Streamline Your Address Management?</h2>
          <p>Join the businesses saving time and improving data accuracy with ZipCode AutoFill.</p>
          <Link to="/register" className="btn btn-primary">Sign Up Now</Link>
          <div className="cta-shape cta-shape-1"></div>
          <div className="cta-shape cta-shape-2"></div>
        </div>
      </section>

      <footer className="disclaimer">
        <p>ZipCode AutoFill uses Google's Geocoding API to provide address information for 5-digit US ZIP codes and, to a limited degree, Canadian postal codes. While we strive for accuracy, we cannot guarantee 100% perfection. Please verify all auto-filled information for critical communications, and report any inaccuracies.</p>
      </footer>
    </div>
  );
}

export default Home;
